/**
 * Kiosk Logger - Simple logging system that queues logs when offline
 * and sends them to the server when online.
 */

const LOG_STORAGE_KEY = 'kiosk_logs_queue';
const MAX_QUEUE_SIZE = 1000; // Prevent localStorage from getting too large
const SEND_BATCH_SIZE = 50; // Send logs in batches

/**
 * Get the current queue of logs from localStorage
 */
function getLogQueue() {
  try {
    const stored = localStorage.getItem(LOG_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error reading log queue:', error);
    return [];
  }
}

/**
 * Save the log queue to localStorage
 */
function saveLogQueue(queue) {
  try {
    // Limit queue size to prevent localStorage overflow
    const limitedQueue = queue.slice(-MAX_QUEUE_SIZE);
    localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(limitedQueue));
  } catch (error) {
    console.error('Error saving log queue:', error);
    // If we can't save, try to clear old logs
    try {
      const reducedQueue = queue.slice(-Math.floor(MAX_QUEUE_SIZE / 2));
      localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(reducedQueue));
    } catch (retryError) {
      console.error('Failed to save reduced log queue:', retryError);
    }
  }
}

/**
 * Add a log entry to the queue
 */
function addLog(level, message, data = {}) {
  const logEntry = {
    timestamp: new Date().toISOString(),
    level, // 'info', 'warn', 'error'
    message,
    data,
    userAgent: navigator.userAgent,
    url: window.location.href,
  };

  const queue = getLogQueue();
  queue.push(logEntry);
  saveLogQueue(queue);

  // Try to send logs if online
  if (navigator.onLine) {
    sendLogsToServer();
  }
}

/**
 * Send queued logs to the server
 */
async function sendLogsToServer() {
  if (!navigator.onLine) {
    return; // Don't try if offline
  }

  const queue = getLogQueue();
  if (queue.length === 0) {
    return; // Nothing to send
  }

  // Send logs in batches
  const batch = queue.slice(0, SEND_BATCH_SIZE);
  const remaining = queue.slice(SEND_BATCH_SIZE);

  try {
    const response = await fetch('/api/kiosk-logs', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ logs: batch }),
    });

    if (response.ok) {
      // Successfully sent, remove from queue
      saveLogQueue(remaining);
      
      // If there are more logs, send the next batch after a short delay
      if (remaining.length > 0) {
        setTimeout(() => sendLogsToServer(), 100);
      }
    } else {
      console.warn('Failed to send logs to server:', response.status);
    }
  } catch (error) {
    // Network error - logs will be sent when connection is restored
    console.warn('Error sending logs to server:', error.message);
  }
}

/**
 * Initialize the logger - set up online/offline listeners
 */
function initLogger() {
  // Send any queued logs when coming back online
  window.addEventListener('online', () => {
    console.log('Connection restored, sending queued logs...');
    sendLogsToServer();
  });

  // Expected kiosk state — use info so DevTools does not show a false "warning"
  window.addEventListener('offline', () => {
    logger.info('Offline: kiosk logs are queued until the connection returns');
  });

  // Try to send logs on initialization if online
  if (navigator.onLine) {
    // Small delay to ensure page is fully loaded
    setTimeout(() => sendLogsToServer(), 2000);
  }
}

/**
 * Logger API
 */
export const logger = {
  /**
   * Log an info message
   */
  info(message, data) {
    console.log(`[Kiosk Logger] ${message}`, data || '');
    addLog('info', message, data);
  },

  /**
   * Log a warning
   */
  warn(message, data) {
    console.warn(`[Kiosk Logger] ${message}`, data || '');
    addLog('warn', message, data);
  },

  /**
   * Log an error
   */
  error(message, data) {
    console.error(`[Kiosk Logger] ${message}`, data || '');
    addLog('error', message, data);
  },

  /**
   * Initialize the logger (call this once when the app starts)
   */
  init: initLogger,
};
